/**
 * Lap Comparison Preprocessor
 *
 * Main entry point for preprocessing laps for comparison.
 * Uses two-phase processing for optimal React hook performance.
 */

import { Lap } from '../../domain/lap/Lap'
import {
  calculateTimeDrift,
  getFastestLap,
} from '../../domain/lap/TimeDriftCalculator'
import {
  executeCoastingPipeline,
  buildCoastingMap,
  DEFAULT_COASTING_CONFIG,
  createVehicleStrategy,
} from '../coasting'
import {
  LapComparisonCache,
  LapComparisonProcessorOptions,
  PreprocessedLapComparison,
} from './types'
import { calculateComparisonStats } from './statistics'
import {
  computeBaseDataPoints,
  applyTimeDriftOverlay,
} from './twoPhaseProcessing'

/**
 * Generate a cache key for a set of laps
 */
export function generateLapComparisonCacheKey(lapIds: string[]): string {
  return [...lapIds].sort().join(':')
}

/**
 * Preprocess laps for comparison view
 *
 * This is the main entry point that does all expensive computation once:
 * - Coasting detection (DSP pipeline)
 * - Time drift calculation
 * - Statistical analysis
 *
 * Uses two-phase processing internally:
 * - Phase 1: Compute base data points (expensive, cached)
 * - Phase 2: Apply time drift overlay (cheap, selection-dependent)
 *
 * @param domainLaps Array of Lap domain objects
 * @param selectedLapIds Optional set of currently selected laps (for time drift reference)
 * @param options Processing options
 * @returns Preprocessed comparison data ready for rendering
 */
export function preprocessLapsForComparison(
  domainLaps: Lap[],
  selectedLapIds?: Set<string>,
  options: LapComparisonProcessorOptions = {}
): LapComparisonCache {
  const {
    vehicleType = 'motorcycle',
    timeDriftSmoothingWindow = 15,
    includeCoasting = true,
    sessionMetadata,
  } = options

  if (domainLaps.length === 0) {
    return {
      cacheKey: '',
      laps: [],
      referenceLapId: null,
      generatedAt: new Date(),
      stats: null,
    }
  }

  // Filter to selected laps for reference calculation
  const lapsForComparison =
    selectedLapIds && selectedLapIds.size > 0
      ? domainLaps.filter((lap) => selectedLapIds.has(lap.id))
      : domainLaps

  // Find fastest lap as reference for time drift
  const referenceLap =
    lapsForComparison.length >= 2 ? getFastestLap(lapsForComparison) : null

  // Pre-calculate time drift for all comparison laps
  const timeDriftMap = new Map<string, ReturnType<typeof calculateTimeDrift>>()
  if (referenceLap && lapsForComparison.length >= 2) {
    for (const lap of lapsForComparison) {
      if (lap.id !== referenceLap.id) {
        const driftResult = calculateTimeDrift(referenceLap, lap, {
          smoothingWindow: timeDriftSmoothingWindow,
        })
        timeDriftMap.set(lap.id, driftResult)
      }
    }
  }

  // Pre-calculate coasting for all laps
  const coastingMaps = new Map<string, Map<number, number>>()
  if (includeCoasting) {
    const vehicleStrategy = createVehicleStrategy(vehicleType)

    for (const lap of domainLaps) {
      if (lap.spatialBuckets && lap.spatialBuckets.length > 0) {
        const coastingPeriods = executeCoastingPipeline(
          [lap],
          DEFAULT_COASTING_CONFIG,
          vehicleStrategy
        )
        coastingMaps.set(lap.id, buildCoastingMap(coastingPeriods))
      }
    }
  }

  // Process each lap using two-phase approach
  const preprocessedLaps: PreprocessedLapComparison[] = domainLaps.map(
    (lap) => {
      const isReference = referenceLap?.id === lap.id
      const timeDriftResult = timeDriftMap.get(lap.id)
      const coastingMap = coastingMaps.get(lap.id) ?? new Map<number, number>()

      // Phase 1: Compute base data points (expensive)
      const baseDataPoints = computeBaseDataPoints(lap, coastingMap)

      // Phase 2: Apply time drift overlay (cheap)
      // Note: For preprocessLapsForComparison, we don't do cross-session recalibration
      // That's handled by the consumer hook when needed
      const dataPoints = applyTimeDriftOverlay(
        baseDataPoints,
        timeDriftResult,
        undefined, // No recalibration in basic preprocessing
        undefined // No batch recalibrate function
      )

      const metadata = sessionMetadata?.get(lap.id)

      return {
        lapId: lap.id,
        lapNumber: lap.lapNumber,
        lapTimeMs: lap.lapTime,
        isReference,
        dataPoints,
        domainLap: lap,
        sessionId: metadata?.sessionId,
        sessionName: metadata?.sessionName,
        device: lap.device,
        hasGyroscope: !!lap.gyro && lap.gyro.length > 0,
        hasAccelerometer: !!lap.accl && lap.accl.length > 0,
      }
    }
  )

  // Calculate statistics
  const stats = calculateComparisonStats(preprocessedLaps)

  return {
    cacheKey: generateLapComparisonCacheKey(domainLaps.map((l) => l.id)),
    laps: preprocessedLaps,
    referenceLapId: referenceLap?.id ?? null,
    generatedAt: new Date(),
    stats,
  }
}
